import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, FileSpreadsheet, FileText, Loader2, Layers, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

/**
 * Group-wise Salary Register — Parent → Sub-Group → CTC drill-down with
 * subtotals at every level. Aggregates `payroll_run_details` server-side.
 *
 * Used inside PayrollRun's bifurcated register pane.
 */
export default function GroupRegisterPanel({ runId, onSelectGroup }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openParents, setOpenParents] = useState(new Set());
  const [openSubs, setOpenSubs] = useState(new Set());
  const [downloading, setDownloading] = useState(null);


  const load = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        `${API_URL}/payroll/runs/${runId}/group-register`,
        { withCredentials: true },
      );
      setData(res);
      // Auto-expand all parents on first load for quick scan.
      setOpenParents(new Set((res.parents || []).map((p) => p.name)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load group register');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (set, key) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  };

  const download = async (format) => {
    setDownloading(format);
    try {
      const res = await axios.get(
        `${API_URL}/payroll/runs/${runId}/group-register/${format}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      const ext = format === 'pdf' ? 'pdf' : 'csv';
      a.href = url;
      a.download = `group_register_run_${data?.run?.run_number || runId}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`${format.toUpperCase()} download failed`);
    } finally {
      setDownloading(null);
    }
  };

  const grand = useMemo(() => data?.grand || {}, [data]);
  const empty = !loading && (!data?.parents?.length);

  const totals = useMemo(() => ({
    employees: grand.employees || 0,
    gross: grand.gross || 0,
    deductions: grand.deductions || 0,
    net: grand.net || 0,
    employer: grand.employer || 0,
    exceptions: grand.exceptions || 0,
    on_hold: grand.on_hold || 0,
  }), [grand]);

  return (
    <div className="bg-white border-y border-slate-200" data-testid="group-register-panel">
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-violet-700" />
          <h3 className="text-sm font-semibold text-slate-800">Group-wise Salary Register</h3>
          <span className="text-[11px] text-slate-500">Parent → Sub-Group → CTC drill-down with subtotals</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="text-slate-500 hover:text-slate-700"
            data-testid="group-reg-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => download('csv')}
            disabled={downloading || empty}
            data-testid="group-reg-csv-btn"
            className="flex items-center gap-1 px-2.5 py-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded text-[11px] font-medium disabled:opacity-50"
          >
            {downloading === 'csv' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />}
            CSV
          </button>
          <button
            onClick={() => download('pdf')}
            disabled={downloading || empty}
            data-testid="group-reg-pdf-btn"
            className="flex items-center gap-1 px-2.5 py-1 bg-rose-100 hover:bg-rose-200 text-rose-800 rounded text-[11px] font-medium disabled:opacity-50"
          >
            {downloading === 'pdf' ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
            PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-xs text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
          Aggregating…
        </div>
      ) : empty ? (
        <div className="p-6 text-center text-xs text-slate-400">
          Process the run to see the group-wise rollup.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="group-reg-table">
            <thead>
              <tr className="bg-slate-100 text-slate-700 text-[11px] uppercase tracking-wider">
                <th className="px-3 py-2 text-left font-semibold">Parent / Sub / CTC</th>
                <th className="px-3 py-2 text-right font-semibold">Emp.</th>
                <th className="px-3 py-2 text-right font-semibold text-emerald-700">Gross</th>
                <th className="px-3 py-2 text-right font-semibold text-rose-700">Deductions</th>
                <th className="px-3 py-2 text-right font-semibold text-[#0052FF]">Net</th>
                <th className="px-3 py-2 text-right font-semibold">Employer</th>
                <th className="px-3 py-2 text-right font-semibold">Exc.</th>
                <th className="px-3 py-2 text-right font-semibold">Hold</th>
              </tr>
            </thead>
            <tbody>
              {data.parents.map((p) => {
                const isOpen = openParents.has(p.name);
                return (
                  <React.Fragment key={p.name}>
                    <tr
                      className="bg-blue-50 hover:bg-blue-100 cursor-pointer font-semibold border-y border-blue-200"
                      onClick={() => setOpenParents(toggle(openParents, p.name))}
                      data-testid={`group-reg-parent-${p.name.replace(/\s+/g, '-')}`}
                    >
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1">
                          {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          {p.name}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{p.employees}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmt(p.gross)}</td>
                      <td className="px-3 py-2 text-right font-mono text-rose-700">{fmt(p.deductions)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[#0052FF]">{fmt(p.net)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700">{fmt(p.employer)}</td>
                      <td className="px-3 py-2 text-right text-rose-700">{p.exceptions || ''}</td>
                      <td className="px-3 py-2 text-right text-amber-700">{p.on_hold || ''}</td>
                    </tr>
                    {isOpen && p.sub_groups.map((s) => {
                      const subKey = `${p.name}>${s.name}`;
                      const subOpen = openSubs.has(subKey);
                      const showSubRow = s.name !== '—';
                      return (
                        <React.Fragment key={subKey}>
                          {showSubRow && (
                            <tr
                              className="bg-blue-25 hover:bg-blue-50 cursor-pointer text-slate-700"
                              onClick={() => setOpenSubs(toggle(openSubs, subKey))}
                              data-testid={`group-reg-sub-${subKey.replace(/[^a-z0-9]/gi, '-')}`}
                            >
                              <td className="px-3 py-1.5 pl-6">
                                <span className="inline-flex items-center gap-1">
                                  {subOpen ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                                  {s.name}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-right">{s.employees}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{fmt(s.gross)}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-rose-700">{fmt(s.deductions)}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-[#0052FF]">{fmt(s.net)}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{fmt(s.employer)}</td>
                              <td className="px-3 py-1.5 text-right text-rose-700">{s.exceptions || ''}</td>
                              <td className="px-3 py-1.5 text-right text-amber-700">{s.on_hold || ''}</td>
                            </tr>
                          )}
                          {(!showSubRow || subOpen) && s.ctc_groups.map((c) => (
                            <tr
                              key={c.id || c.code}
                              className="hover:bg-slate-50 cursor-pointer text-slate-600"
                              onClick={() => onSelectGroup && onSelectGroup(c)}
                              data-testid={`group-reg-leaf-${c.code || c.id}`}
                              title="Click to filter the bifurcated register below by this CTC group"
                            >
                              <td className="px-3 py-1 pl-12">
                                <span className="font-mono text-[10px] text-blue-700 mr-1">{c.code}</span>
                                {c.name}
                              </td>
                              <td className="px-3 py-1 text-right">{c.employees}</td>
                              <td className="px-3 py-1 text-right font-mono text-emerald-700">{fmt(c.gross)}</td>
                              <td className="px-3 py-1 text-right font-mono text-rose-700">{fmt(c.deductions)}</td>
                              <td className="px-3 py-1 text-right font-mono text-[#0052FF]">{fmt(c.net)}</td>
                              <td className="px-3 py-1 text-right font-mono">{fmt(c.employer)}</td>
                              <td className="px-3 py-1 text-right text-rose-700">{c.exceptions || ''}</td>
                              <td className="px-3 py-1 text-right text-amber-700">{c.on_hold || ''}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <tr className="bg-slate-900 text-white font-bold border-t-2 border-slate-900" data-testid="group-reg-grand-total">
                <td className="px-3 py-2">GRAND TOTAL</td>
                <td className="px-3 py-2 text-right">{totals.employees}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-300">{fmt(totals.gross)}</td>
                <td className="px-3 py-2 text-right font-mono text-rose-300">{fmt(totals.deductions)}</td>
                <td className="px-3 py-2 text-right font-mono text-blue-300">{fmt(totals.net)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(totals.employer)}</td>
                <td className="px-3 py-2 text-right">{totals.exceptions || 0}</td>
                <td className="px-3 py-2 text-right">{totals.on_hold || 0}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
